import type { Table } from "@tanstack/react-table"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react"

interface DataGridPaginationProps<T> {
  table: Table<T>
  totalRows: number
}

export function DataGridPagination<T>({ table, totalRows }: DataGridPaginationProps<T>) {
  const pageIndex = table.getState().pagination.pageIndex
  const pageSize = table.getState().pagination.pageSize
  const pageCount = table.getPageCount()
  const filteredRows = table.getFilteredRowModel().rows.length

  // What a page actually holds. The grid sets paginateExpandedRows: false, so getExpandedRowModel
  // returns the model unexpanded and pagination slices TOP-LEVEL rows — which are group rows once
  // grouping is on. getPageCount() is derived from the same count, so this is the unit the controls
  // move through; measuring the range against the leaf-row count instead would report a page of
  // 50 groups as "1–50 of 3000 rows" next to "Page 1 of 1".
  const isGrouped = table.getState().grouping.length > 0
  const pagedUnits = table.getPrePaginationRowModel().rows.length

  return (
    <nav aria-label="Table pagination" className="flex items-center justify-between border-t border-border bg-background px-4 py-2">
      <div className="text-xs text-muted-foreground" aria-live="polite">
        Showing {pagedUnits === 0 ? 0 : pageIndex * pageSize + 1}–
        {Math.min((pageIndex + 1) * pageSize, pagedUnits)} of {pagedUnits} {isGrouped ? 'groups' : 'rows'}
        {isGrouped && ` · ${filteredRows} rows`}
        {filteredRows !== totalRows && ` (filtered from ${totalRows})`}
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          {/* The number picked is groups per page while grouping is on, so say so */}
          <label htmlFor="rows-per-page" className="text-xs text-muted-foreground">
            {isGrouped ? 'Groups:' : 'Rows:'}
          </label>
          <Select
            value={String(pageSize)}
            onValueChange={(val) => table.setPageSize(Number(val))}
          >
            <SelectTrigger id="rows-per-page" className="h-7 w-[65px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[25, 50, 100].map((size) => (
                <SelectItem key={size} value={String(size)} className="text-xs">
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1" role="group" aria-label="Pagination controls">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            aria-label="First page"
          >
            <ChevronsLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs text-muted-foreground px-2" aria-live="polite" aria-atomic="true">
            Page {pageIndex + 1} of {pageCount}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label="Next page"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => table.setPageIndex(pageCount - 1)}
            disabled={!table.getCanNextPage()}
            aria-label="Last page"
          >
            <ChevronsRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </nav>
  )
}
